import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSignals, getStats } from '../api/client';
import { familyLabel, familyClassName, formatDate, eventDate, isEventDateEstimated } from '../familyMeta';
import { COLLECTOR_SOURCE_IDS, collectorSourceOptions } from '../sourceMeta';
import Pager, { PAGE_SIZE } from '../components/Pager';
import { ExternalLink } from 'lucide-react';

export default function SignalFeed() {
  const [filters, setFilters] = useState({
    signal_type: '',
    signal_family: '',
    source: '',
    date_from: '',
    date_to: '',
  });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ types: [], families: [], sources: [] });

  useEffect(() => {
    getStats({ sources: COLLECTOR_SOURCE_IDS })
      .then((stats) =>
        setFilterOptions({
          types: Object.keys(stats.by_signal_type).sort(),
          families: Object.keys(stats.by_signal_family).sort(),
          sources: collectorSourceOptions(stats.by_source),
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    setError(null);
    getSignals({ ...filters, sources: COLLECTOR_SOURCE_IDS, limit: PAGE_SIZE, offset })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [filters, offset]);

  function updateFilter(key, value) {
    setOffset(0);
    setFilters((f) => ({ ...f, [key]: value }));
  }

  return (
    <>
      <div className="filter-bar">
        <label>
          Signal type
          <select value={filters.signal_type} onChange={(e) => updateFilter('signal_type', e.target.value)}>
            <option value="">All types</option>
            {filterOptions.types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label>
          Family
          <select value={filters.signal_family} onChange={(e) => updateFilter('signal_family', e.target.value)}>
            <option value="">All families</option>
            {filterOptions.families.map((f) => (
              <option key={f} value={f}>{familyLabel(f)}</option>
            ))}
          </select>
        </label>
        <label>
          Source
          <select value={filters.source} onChange={(e) => updateFilter('source', e.target.value)}>
            <option value="">All sources</option>
            {filterOptions.sources.map(({ source, label }) => (
              <option key={source} value={source}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          From
          <input type="date" value={filters.date_from} onChange={(e) => updateFilter('date_from', e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={filters.date_to} onChange={(e) => updateFilter('date_to', e.target.value)} />
        </label>
      </div>

      {error && <div className="state-msg error">Couldn't load signals: {error}</div>}

      {!error && data && (
        <>
          <table className="signal-table">
            <colgroup>
              <col className="signal-col-company" />
              <col className="signal-col-type" />
              <col className="signal-col-family" />
              <col className="signal-col-confidence" />
              <col className="signal-col-evidence" />
              <col className="signal-col-date" />
            </colgroup>
            <thead>
              <tr>
                <th>Company</th>
                <th>Signal type</th>
                <th>Family</th>
                <th>Confidence</th>
                <th>Evidence</th>
                <th title="Real event date when the source provides one; falls back to the date we collected it otherwise.">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((sig) => (
                <tr key={sig.id}>
                  <td className="signal-company-cell">
                    {sig.account ? (
                      <>
                        <Link className="company-link" title={sig.account.name} to={`/accounts/${sig.account.id}`}>
                          {sig.account.name}
                        </Link>
                        {sig.account.domain && <div className="company-meta" title={sig.account.domain}>{sig.account.domain}</div>}
                      </>
                    ) : (
                      <span className="unattributed-pill">Unattributed / market signal</span>
                    )}
                  </td>
                  <td className="signal-type-cell">{sig.signal_type.replace(/_/g, ' ')}</td>
                  <td className="signal-family-cell">
                    <span className={`badge ${familyClassName(sig.signal_family)}`}>
                      {familyLabel(sig.signal_family)}
                    </span>
                  </td>
                  <td className="conf">{sig.confidence != null ? sig.confidence.toFixed(2) : '—'}</td>
                  <td className="signal-evidence-cell">
                    {sig.evidence_url ? (
                      <a className="evidence-link" href={sig.evidence_url} target="_blank" rel="noreferrer">
                        View source <ExternalLink size={13} aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="no-evidence">no link</span>
                    )}
                  </td>
                  <td className="signal-date-cell">
                    {formatDate(eventDate(sig))}
                    {isEventDateEstimated(sig) && (
                      <div className="company-meta" title="No published date from the source — this is when we collected it.">(collected)</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.items.length === 0 && (
            <div className="state-msg">No signals match these filters.</div>
          )}

          <Pager page={Math.floor(offset / PAGE_SIZE) + 1} total={data.total} onPage={(p) => setOffset((p - 1) * PAGE_SIZE)} />
        </>
      )}

      {!error && !data && <div className="state-msg">Loading…</div>}
    </>
  );
}
